/**
 * Browser-window chrome around the approver UI (SKE-4166, demo UX uplift).
 *
 * On the desktop two-pane this is the "what your client sees" screen: the real
 * approver `App` sitting inside a faux browser window — three traffic-light
 * dots and a read-only URL pill — so a visitor reads it as one of two devices,
 * beside the "Your Sked" team screen (see TeamChrome).
 *
 * STATIC ONLY — NO TRANSFORM
 * --------------------------
 * The approver `App` renders a `position: fixed` confetti canvas, and a
 * transform on any ancestor would re-anchor it (see the containing-block note
 * in demoReveal.ts). This frame therefore uses border / shadow / background /
 * padding only, never transform / scale / zoom, and neither does anything that
 * wraps it. The only motion in the demo shell is opacity.
 *
 * Tailwind only (styled-components are lint-blocked in this app); Font Awesome
 * for the (decorative) lock glyph.
 */

import { cn } from '@dialogueconsulting/sked-ui/lib/utils'

import { demoFormat, demoStrings } from './demoStrings'
import { useDemoBrand } from './useDemoBrand'

/** Fall-back slug when the demo has not been personalized to a client. */
const DEFAULT_BRAND_SLUG = 'marigold'

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)

const ClientFrame: React.FC<React.PropsWithChildren> = ({ children }) => {
  const brand = useDemoBrand()

  // The faux share URL is keyed to the applied brand when there is one, so a
  // personalized board reads as that client's own review link.
  const slug = brand
    ? slugify(brand.name) || slugify(brand.domain) || DEFAULT_BRAND_SLUG
    : DEFAULT_BRAND_SLUG

  // The always-on honesty disclosure lives here in the browser chrome on the
  // two-pane (the floating DemoHonestyBadge hides itself >= 1200 to avoid
  // colliding with the team pane). Copy is the canonical label from
  // demoStrings, brand-aware exactly like the badge.
  const honestyLabel = brand
    ? demoFormat('honestyBadgeBrandedLabel', { brand: brand.name })
    : demoStrings.honestyBadgeLabel

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-[0_12px_40px_-12px_rgba(16,24,40,0.25)]">
      {/* Persistent side identity: a warm (spice) hairline marks this as the
          client's screen — the counterpart to TeamChrome's blurple hairline. */}
      <div aria-hidden="true" className="h-[3px] shrink-0 bg-spice-500" />

      {/* Browser chrome bar — traffic lights + read-only address pill. */}
      <div className="flex shrink-0 items-center gap-3 border-b border-neutral-200 bg-neutral-50 px-3 py-2">
        <div className="flex shrink-0 items-center gap-1.5" aria-hidden="true">
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]" />
        </div>

        {/* Names the side in warm accent, so the split reads without the tour. */}
        <span className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-spice-700">
          <i aria-hidden="true" className="fa-solid fa-link text-[10px]" />
          {demoStrings.sideClientLabel}
        </span>

        <div className="mx-auto flex min-w-0 max-w-[22rem] flex-1 items-center justify-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-500">
          <i
            aria-hidden="true"
            className="fa-solid fa-lock text-[10px] text-neutral-400"
          />
          <span className="truncate">collab.skedsocial.com/r/{slug}</span>
        </div>

        {/* Always-on honesty disclosure, in the browser chrome so it reads as
            "this window is a demo" and never overlaps the team pane. */}
        <span
          role="note"
          aria-label={demoStrings.honestyBadgeAriaLabel}
          data-testid="demo-honesty-chip"
          className="ml-auto inline-flex max-w-[45%] shrink-0 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-600"
        >
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500"
          />
          <span className="truncate">{honestyLabel}</span>
        </span>
      </div>

      <div className={cn('min-h-0 flex-1 overflow-hidden')}>{children}</div>
    </div>
  )
}

export default ClientFrame
